/**
 * Narrative Parser — Shared text-structure parser for AI-generated narratives
 *
 * Parses plain-text AI narratives into structured sections with headers,
 * paragraphs, and numbered steps. Used by both web and mobile to render
 * formatted strategy insights instead of a single text blob.
 *
 * Detected patterns:
 *   - Section headers: ALL-CAPS with colon, or keyword phrases with colon
 *   - Numbered steps: (1), (2), (3) format
 *   - Opening/closing paragraphs: free-form text
 */

// =============================================================================
// Types
// =============================================================================

export interface NarrativeStep {
  /** 1-based step number */
  number: number;
  /** Step content text */
  content: string;
}

export interface NarrativeSection {
  /** Section header text, or null for the intro paragraph(s) */
  title: string | null;
  /** Free-form paragraphs before any numbered steps */
  paragraphs: string[];
  /** Ordered numbered steps within this section */
  steps: NarrativeStep[];
}

export interface ParsedNarrative {
  /** Ordered array of sections */
  sections: NarrativeSection[];
}

// =============================================================================
// Header Detection
// =============================================================================

/**
 * Known keyword prefixes that signal a section header when followed by
 * optional words and a colon. Case-insensitive matching.
 */
const HEADER_KEYWORDS = [
  'step-by-step',
  'implementation',
  'measurement',
  'why this fits',
  "here's why",
  "here's the reality",
  'how to',
  'start this week',
  'key insight',
  'bottom line',
  'recommendation',
  'next steps',
];

/**
 * Detect whether a candidate string looks like a section header.
 * Two patterns:
 *   1. ALL-CAPS text (4+ chars) ending with colon
 *   2. Keyword phrase ending with colon
 */
function isHeaderCandidate(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed.endsWith(':')) return false;

  const beforeColon = trimmed.slice(0, -1).trim();
  if (beforeColon.length < 3) return false;

  // Pattern 1: ALL-CAPS header (at least 4 alpha chars)
  if (/^[A-Z][A-Z\s']{3,}$/.test(beforeColon)) return true;

  // Pattern 2: Keyword-based header
  const lower = beforeColon.toLowerCase();
  return HEADER_KEYWORDS.some((kw) => lower.startsWith(kw));
}

// =============================================================================
// Step Extraction
// =============================================================================

/** Regex to match numbered step markers: (1), (2), etc. */
const STEP_PATTERN = /\((\d+)\)\s*/g;

/**
 * Extract numbered steps and leading paragraphs from a text segment.
 */
function extractStepsAndParagraphs(text: string): {
  paragraphs: string[];
  steps: NarrativeStep[];
} {
  const steps: NarrativeStep[] = [];
  const matches: { index: number; number: number; matchLength: number }[] = [];

  for (const match of text.matchAll(STEP_PATTERN)) {
    matches.push({
      index: match.index,
      number: parseInt(match[1], 10),
      matchLength: match[0].length,
    });
  }

  if (matches.length === 0) {
    // No steps — everything is paragraph text
    const trimmed = text.trim();
    return {
      paragraphs: trimmed ? [trimmed] : [],
      steps: [],
    };
  }

  // Text before the first step marker is paragraph content
  const beforeSteps = text.slice(0, matches[0].index).trim();
  const paragraphs = beforeSteps ? [beforeSteps] : [];

  // Extract each step's content
  for (let i = 0; i < matches.length; i++) {
    const start = matches[i].index + matches[i].matchLength;
    const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
    const content = text.slice(start, end).trim();
    if (content) {
      steps.push({ number: matches[i].number, content });
    }
  }

  return { paragraphs, steps };
}

// =============================================================================
// Main Parser
// =============================================================================

/**
 * Parse an AI narrative string into structured sections.
 * Handles section headers (ALL-CAPS or keyword-based), numbered steps,
 * and free-form paragraphs.
 */
export function parseNarrative(text: string | null | undefined): ParsedNarrative {
  if (!text || !text.trim()) {
    return { sections: [] };
  }

  const trimmed = text.trim();

  // Strategy: scan through the text looking for header boundaries.
  // A header is recognized when it appears after a sentence end (". ") or
  // at the start of text, and the phrase before the colon matches our patterns.
  const segments: { title: string | null; body: string }[] = [];
  const remaining = trimmed;
  let foundHeaders = false;

  // Try to find headers by scanning for colon positions
  // We look for ". HEADER:" or start-of-string "HEADER:" patterns
  const headerPositions: { index: number; title: string; endIndex: number }[] = [];

  // Scan for potential headers
  const colonRegex = /:\s*/g;
  for (const colonMatch of remaining.matchAll(colonRegex)) {
    const colonIdx = colonMatch.index;

    // Look backwards from the colon to find the start of the potential header
    // Headers start after ". " or at start of string
    let headerStart = -1;

    // Check backwards for ". " boundary
    for (let i = colonIdx - 1; i >= 0; i--) {
      if (i >= 1 && remaining[i - 1] === '.' && remaining[i] === ' ') {
        headerStart = i + 1;
        break;
      }
      if (i === 0) {
        headerStart = 0;
        break;
      }
    }

    if (headerStart === -1) continue;

    const candidate = remaining.slice(headerStart, colonIdx + 1);
    if (isHeaderCandidate(candidate)) {
      headerPositions.push({
        index: headerStart,
        title: candidate.slice(0, -1).trim(), // Remove trailing colon
        endIndex: colonIdx + colonMatch[0].length,
      });
    }
  }

  if (headerPositions.length === 0) {
    // No headers found — single section with the entire text
    const { paragraphs, steps } = extractStepsAndParagraphs(remaining);
    return {
      sections: [{ title: null, paragraphs, steps }],
    };
  }

  foundHeaders = true;

  // Build segments from header positions
  // First segment: text before the first header
  if (headerPositions[0].index > 0) {
    const introText = remaining.slice(0, headerPositions[0].index).trim();
    if (introText) {
      segments.push({ title: null, body: introText });
    }
  }

  // Each header creates a segment
  for (let i = 0; i < headerPositions.length; i++) {
    const hp = headerPositions[i];
    const nextStart =
      i + 1 < headerPositions.length ? headerPositions[i + 1].index : remaining.length;
    const body = remaining.slice(hp.endIndex, nextStart).trim();
    segments.push({ title: hp.title, body });
  }

  // Convert segments to sections
  const sections: NarrativeSection[] = [];
  for (const segment of segments) {
    const { paragraphs, steps } = extractStepsAndParagraphs(segment.body);
    // Only include non-empty sections
    if (paragraphs.length > 0 || steps.length > 0) {
      sections.push({ title: segment.title, paragraphs, steps });
    }
  }

  // If we found headers but all sections ended up empty, fall back
  if (foundHeaders && sections.length === 0) {
    const { paragraphs, steps } = extractStepsAndParagraphs(remaining);
    return {
      sections: [{ title: null, paragraphs, steps }],
    };
  }

  return { sections };
}

/**
 * Check if a parsed narrative has meaningful structure (multiple sections or steps).
 * Callers can use this to decide between structured rendering and plain text fallback.
 */
export function hasStructuredContent(parsed: ParsedNarrative): boolean {
  if (parsed.sections.length > 1) return true;
  if (parsed.sections.length === 1 && parsed.sections[0].steps.length > 0) return true;
  return false;
}
